import { useState } from "react";
import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTAFooter from "@/components/CTAFooter";
import { specialProducts, SpecialProduct } from "@/data/specialsContent";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

const typeFilters = ["ALL", "SPLIT", "DUCTED", "MULTI"] as const;
const brandFilters = ["All Brands", "Daikin", "Fujitsu", "ActronAir", "Samsung", "LG", "Mitsubishi", "Rinnai"] as const;

const SpecialCard = ({ product }: { product: SpecialProduct }) => {
  const [showTerms, setShowTerms] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col h-full border border-gray-100 hover:shadow-xl transition-shadow">
      {/* Badge */}
      {product.badge && (
        <div className="px-6 pt-4">
          <Badge className={`text-xs font-bold uppercase ${product.badge === "BEST SELLER" ? "bg-primary text-primary-foreground" : "bg-orange-500 text-white"}`}>
            {product.badge}
          </Badge>
        </div>
      )}

      {/* Brand Logo */}
      {product.brandLogo && (
        <div className="px-6 pt-4 flex items-center gap-2">
          <img src={product.brandLogo} alt={product.brand} className="h-8 object-contain" />
        </div>
      )}

      {/* Product Image */}
      <div className="flex items-center justify-center p-6">
        <img
          src={product.image}
          alt={`${product.brand} ${product.model}`}
          className="h-48 object-contain"
        />
      </div>

      {/* Content */}
      <div className="px-6 pb-6 flex flex-col flex-1">
        <h3 className="text-xl font-bold text-foreground">
          {product.brand} {product.type === "multi" ? product.model : `${product.type === "ducted" ? "Ducted" : "Split"} System`}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">Model: {product.model}</p>

        {product.capacity && (
          <p className="text-sm font-semibold text-primary mt-2">{product.capacity}</p>
        )}

        {product.outlets && (
          <p className="text-sm text-muted-foreground mt-1 font-medium">{product.outlets}</p>
        )}

        {/* Capacity Options */}
        {product.capacityOptions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {product.capacityOptions.map((cap) => (
              <span key={cap} className="text-xs bg-muted px-2 py-1 rounded font-medium">
                {cap}
              </span>
            ))}
          </div>
        )}

        {/* Pricing */}
        <div className="mt-4">
          <p className="text-xs text-muted-foreground uppercase font-semibold">Supply & Install</p>
          {product.oldPrice && (
            <p className="text-sm text-muted-foreground line-through">{product.oldPrice}</p>
          )}
          <p className="text-2xl font-bold text-primary">{product.newPrice || "Call for Price"}</p>
          {product.suitableFor && (
            <p className="text-xs text-muted-foreground mt-1">{product.suitableFor}</p>
          )}
          {product.priceNote && (
            <p className="text-xs text-muted-foreground">{product.priceNote}</p>
          )}
        </div>

        {/* Terms */}
        {product.terms.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowTerms(!showTerms)}
              className="flex items-center gap-1 text-xs text-primary font-semibold hover:underline"
            >
              Terms & Conditions
              {showTerms ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showTerms && (
              <ul className="mt-2 space-y-1">
                {product.terms.map((term, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Check className="w-3 h-3 text-primary flex-shrink-0 mt-0.5" />
                    {term}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="mt-auto pt-4">
          <a
            href="#"
            className="block w-full text-center bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-3 px-6 rounded-lg transition-colors text-sm uppercase tracking-wide"
          >
            Reserve Now
          </a>
        </div>
      </div>
    </div>
  );
};

const Specials = () => {
  const [activeType, setActiveType] = useState<string>("ALL");
  const [activeBrand, setActiveBrand] = useState<string>("All Brands");

  const filtered = specialProducts.filter((p) => {
    const typeMatch = activeType === "ALL" || p.type === activeType.toLowerCase();
    const brandMatch = activeBrand === "All Brands" || p.brand === activeBrand;
    return typeMatch && brandMatch;
  });

  return (
    <div className="min-h-screen bg-background">
      <PageSEO title="AC Specials & Deals | ACG Southern Highlands" description="Save on split systems, ducted AC and multi-head units from Daikin, Fujitsu, Samsung and more. View current the Southern Highlands air conditioning deals and promotions." />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Specials" }]} />

      {/* Hero Banner */}
      <section className="relative bg-gradient-to-r from-primary to-acg-charcoal text-primary-foreground py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Special Offers & Promotions
          </h1>
          <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto">
            Save on our Air Conditioning Deals! Free No-Obligation Onsite Quotation. Same Day Installations – Servicing the Southern Highlands.
          </p>
        </div>
        <img
          src="https://airconditioningguys.com.au/uploads/autumn-sale-2025-special.png"
          alt="Autumn Sale 2025"
          className="mx-auto mt-6 max-w-3xl w-full px-4"
        />
      </section>

      {/* Filters */}
      <section className="bg-muted py-6 border-b">
        <div className="container mx-auto px-4">
          {/* Type Filter */}
          <div className="flex flex-wrap gap-2 mb-4 justify-center">
            {typeFilters.map((t) => (
              <button
                key={t}
                onClick={() => setActiveType(t)}
                className={`px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wide transition-colors ${
                  activeType === t
                    ? "bg-primary text-primary-foreground"
                    : "bg-white text-foreground border border-border hover:bg-accent"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {/* Brand Filter */}
          <div className="flex flex-wrap gap-2 justify-center">
            {brandFilters.map((b) => (
              <button
                key={b}
                onClick={() => setActiveBrand(b)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  activeBrand === b
                    ? "bg-acg-charcoal text-primary-foreground"
                    : "bg-white text-foreground border border-border hover:bg-accent"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground text-lg py-12">No specials found for this selection.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((product, idx) => (
                <SpecialCard key={`${product.brand}-${product.model}-${idx}`} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>


      {/* Buying Guide Content */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-black text-foreground mb-6">Best Time to Buy and Install</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            In the Southern Highlands, the best time to buy and install a new heating and cooling system is generally before the busiest seasonal rush. Booking during autumn — ahead of the sub-zero mornings that arrive across Mittagong, Bowral and Moss Vale — usually means shorter lead times, more installer availability and time to properly plan the job rather than rushing a decision under pressure.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Waiting until the first hard frost of the season is often when demand peaks and booking diaries fill fastest, which can mean a longer wait for both quotes and installation. Buying early also gives you time to compare options properly, consider financing, and make sure the system chosen is correctly sized for your property's elevation and exposure rather than settling for whatever can be installed quickest.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">What's Typically Included in an Installed Price</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            A genuine supply-and-install quote should cover more than just the unit itself. Understanding what's generally bundled in — and what commonly sits outside it — helps you compare quotes properly:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
            <li>The indoor and outdoor unit(s), supplied and matched to your property</li>
            <li>Standard installation labour, including mounting brackets and a standard-length refrigerant line set</li>
            <li>Basic electrical connection to an existing, adequate power supply</li>
            <li>Standard condensate drainage arrangements</li>
            <li>Commissioning, testing and a run-through of system operation</li>
            <li>Manufacturer warranty registration where applicable</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mb-4">
            What commonly sits outside a "standard" install and may be charged as an extra includes: additional refrigerant pipe runs beyond a standard length, upgraded electrical wiring or a new circuit if your switchboard needs work, difficult access (such as two-storey installs or awkward roof spaces), removal and disposal of an old system, ducting or additional outlets for ducted systems, and any structural work such as wall penetrations in brick veneer or heritage-listed homes.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Questions to Ask Any Installer</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Before committing to any quote, it's worth asking a few direct questions so you know exactly what you're paying for:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
            <li>Is this price fixed, or could it change once the installer sees the site?</li>
            <li>Does the quote include disposal of my old unit, or is that extra?</li>
            <li>What length of refrigerant line and electrical run is included before extra charges apply?</li>
            <li>Is the system's heating capacity suitable for our winter minimums and elevation, not just summer cooling?</li>
            <li>What manufacturer warranty applies, and what do I need to do to keep it valid?</li>
            <li>Is the installer's own workmanship guaranteed, and for how long?</li>
            <li>Are you fully licensed to handle refrigerant (ARC licence) and carry appropriate insurance?</li>
          </ul>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Why Quotes Differ Between Installers</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            It's common to receive quotes for the same brand and capacity that vary significantly in price. This usually comes down to what is and isn't included, rather than one installer simply being cheaper. A lower quote may exclude disposal, use a shorter standard pipe run, or allow for a more basic electrical connection than your property actually needs.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Experience and correct sizing also play a part. An installer who properly accounts for your property's elevation, frost exposure and insulation may recommend a different capacity unit than one working from a generic size guide — which affects both the price and how well the system performs on the coldest mornings.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">How to Compare Quotes Like-for-Like</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            To fairly compare two quotes, line up the same details side by side: brand and model, heating and cooling capacity, length of pipe run and electrical work included, whether old unit removal and disposal is covered, the workmanship warranty period, and the manufacturer's warranty term. A quote that looks cheaper on the surface may end up costing more once these inclusions are accounted for.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            It's also worth asking for a written, itemised quote rather than a single lump sum, so you can see exactly what's covered and query anything that seems unclear before work begins.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Upgrade and Replacement Considerations</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            If you're replacing an older system, it's worth considering more than just a like-for-like swap. Older units are often undersized for genuine winter heating loads in the Southern Highlands, or lack the low-ambient heating performance needed to cope with sub-zero mornings efficiently. An upgrade may also be a good opportunity to move to a ducted or multi-head system if your heating needs have grown, or to add wifi control for easier scheduling through winter.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Before replacing a system, ask whether the existing electrical circuit, refrigerant lines and outdoor unit position are still suitable, or whether these will need upgrading as part of the job — this is often where the real cost difference between a basic swap and a proper upgrade comes from. If you're weighing up finance for a larger or upgraded system, see our <a href="/finance-options" className="text-primary font-semibold">finance options</a> page, and if you'd like an on-site assessment, get in touch via our <a href="/contact-us" className="text-primary font-semibold">contact page</a> or call <a href="tel:0480841485" className="text-primary font-semibold">0480 841 485</a>.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Avoiding Common Buying Mistakes</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            One of the most common mistakes property owners make is choosing a system based purely on the lowest advertised price, without checking whether it's correctly sized for genuine winter heating loads in a cold-climate area like the Southern Highlands. A unit that's too small for the space will run constantly and still struggle to keep up on sub-zero mornings, while an oversized unit can cycle inefficiently and cost more to run than necessary.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Another frequent issue is accepting a verbal price over the phone without an on-site assessment. Every property is different — wall construction, ceiling access, existing wiring and outdoor unit placement all affect what's actually required, and a phone quote can't account for these details. A proper on-site inspection helps avoid surprise costs once installation is underway.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Seasonal Promotions vs Everyday Pricing</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Seasonal specials, such as the offers above, can be a genuinely good way to secure a system at a reduced installed price, particularly when booked ahead of peak demand. That said, it's worth checking that any promotional price still includes the same standard inclusions — labour, standard pipe run, commissioning and warranty registration — as an everyday quote, rather than being a stripped-back offer that adds costs back in once you're locked in.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            If a special doesn't suit your capacity or brand needs, it's still worth getting a standard, itemised quote for comparison — sometimes the right-sized system outside a current promotion works out better value once heating performance and running costs over a Southern Highlands winter are factored in.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Planning Ahead for Peace of Mind</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Whichever system you choose, planning the purchase and install with enough lead time before winter gives you the best chance of a smooth installation and a system that's ready to perform when the frosts arrive. Once your new system is installed, keeping it running reliably each winter is easier with scheduled maintenance — see our <a href="/membership" className="text-primary font-semibold">membership page</a> for details on annual pre-winter servicing.
          </p>
        </div>
      </section>

      <CTAFooter />
    </div>
  );
};

export default Specials;
